import { useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "app/hooks";

// Mui
import { Box, Button, Typography, Skeleton, Chip, Tooltip } from "@mui/material";

// Components
import CustomIcon from "components/CustomIcon";
import { RowHead, Row } from "../EntityList/EntityList";
import { useSnackBar, SnackBar } from "components/SnackBar";
import { CircledButton } from "pages/ProjectDashboard/styled-components";
import { ButtonWithCounter } from "components/Buttons";

// Others
import icons from "enums/icons";
import { capitalize } from "lodash";
import { urlPaths } from "enums/urlPaths";
import {
    SnackBarEntityTypes,
    TConvertedPeopleList,
    TConvertedCompaniesList,
    convertProjectPerson,
    convertProjectCompany,
    getBriefList,
} from "pages/ProjectDashboard/adapters/formattedData";
import useRequestSet from "utils/hooks/useRequestSet";

// Store
import { clearChart } from "store/slice/charts";

// Styles
import BriefCardStyles from "./BriefCard.styles";
import { TruncatedText } from "components/TruncatedText";

const selectedEntityInitialState: {
    type: keyof typeof SnackBarEntityTypes | undefined;
    data: (TConvertedPeopleList | TConvertedCompaniesList)[];
} = {
    type: undefined,
    data: [],
};

const BriefCard = ({
    data,
    companyId,
    onClickRelatedButton,
    onClickCreateSearchAi,
}: {
    data: ReturnType<typeof getBriefList>[0];
    companyId: number | undefined;
    onClickRelatedButton: (id: number, type: "insight" | "searchAi" | "brief" | "company" | "people") => void;
    onClickCreateSearchAi?: () => void;
}) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const classes = BriefCardStyles({ isDisabled: data.isDisabled, isCompleted: data.isCompleted });

    const { shouldOpenSnackBar, popOverAnchorEl, handleOpenSnackBar, handleCloseSnackBar } = useSnackBar();
    const { requestSets } = useRequestSet();

    const wrapperRef = useRef<HTMLElement | null | undefined>(null);
    const [selectedEntity, setSelectedEntity] = useState<typeof selectedEntityInitialState>(selectedEntityInitialState);

    const [hoverChipId, setHoverChipId] = useState<number | null>(null);
    const [clickedChipId, setClickedChipId] = useState<number | null>(null);

    const chips = [
        {
            id: 1,
            label: "Company",
            count: data.meta.companies_count,
        },
        {
            id: 2,
            label: "People",
            count: data.meta.people_count,
        },
        {
            id: 3,
            label: "Shortlist",
            count: data.meta.shortlist_count,
        },
    ];

    const handleClickCircledCount = async (
        event: React.MouseEvent<HTMLDivElement, MouseEvent>,
        type: keyof typeof SnackBarEntityTypes,
        chip: typeof chips[0]
    ) => {
        if (chip.count === 0) return;

        setSelectedEntity((prevVal) => ({ ...prevVal, type }));
        handleOpenSnackBar(event);
        setClickedChipId(chip.id);

        if (type === "company") {
            const response = await requestSets(data.meta.projectId, data.relatedLists.company as string[], type);
            const list: TConvertedCompaniesList[] = response.map((p: any) => convertProjectCompany(p.data));
            setSelectedEntity((prevVal) => ({ ...prevVal, data: list }));
        }

        if (type === "people") {
            const response = await requestSets(data.meta.projectId, data.relatedLists.people as string[], type);
            const list: TConvertedPeopleList[] = response.map((p: any) => convertProjectPerson(p.data));
            setSelectedEntity((prevVal) => ({ ...prevVal, data: list }));
        }

        if (type === "shortlist") {
            const response = await requestSets(data.meta.projectId, data.relatedLists.shortList as string[], "people");
            const list: TConvertedPeopleList[] = response.map((p: any) => convertProjectPerson(p.data));
            setSelectedEntity((prevVal) => ({ ...prevVal, data: list }));
        }
    };

    const handleGotoBrief = (path: string) => {
        dispatch(clearChart());
        navigate(
            `${path}?projectId=${data.meta.projectId}&insightId=${data.meta.relatedInsight?.id}&briefId=${data.id}`,
            {
                state: {
                    from: `${urlPaths.ProjectDashboard}/${data.meta.projectId}`,
                },
            }
        );
    };

    const handleCreateSearchAI = () => {
        onClickCreateSearchAi && onClickCreateSearchAi();
    };

    const LoadingData = () => {
        return (
            <>
                <Skeleton variant="rectangular" animation="wave" height={28} sx={{ mb: 0.5 }} />
                <Skeleton variant="rectangular" animation="wave" height={28} sx={{ mb: 0.5 }} />
                <Skeleton variant="rectangular" animation="wave" height={28} sx={{ mb: 0.5 }} />
                <Skeleton variant="rectangular" animation="wave" height={28} sx={{ mb: 0.5 }} />
                <Skeleton variant="rectangular" animation="wave" height={28} />
            </>
        );
    };

    return (
        <>
            <Box className={classes.cardWrapper}>
                <>
                    <Box className={classes.cardInner}>
                        <Box className={classes.mainInfo}>
                            <Box mb={2}>
                                <Typography className={classes.cardTitle}>
                                    <TruncatedText>{data.name}</TruncatedText>
                                </Typography>
                                {!data.isCompleted && (
                                    <Typography className={classes.briefName}>
                                        {data.meta?.relatedInsight?.name}
                                    </Typography>
                                )}
                            </Box>
                            <Box className={classes.actionsWrapper}>
                                {!data.isDisabled && (
                                    <Tooltip
                                        arrow
                                        placement="bottom-start"
                                        title={intl.formatMessage({ id: "dashboard.insights.eyeButton.tooltip" })}
                                    >
                                        <CircledButton
                                            size={data.isCompleted ? 24 : 28}
                                            color="hovered2"
                                            component="button"
                                            onClick={() => handleGotoBrief(`${urlPaths.BriefReview}/${companyId}`)}
                                        >
                                            <CustomIcon
                                                icon={icons.eye}
                                                sx={{
                                                    fontSize: 14,
                                                    color: "button.active",
                                                    "&:hover": {
                                                        backgroundColor: "button.active",
                                                    },
                                                }}
                                            />
                                        </CircledButton>
                                    </Tooltip>
                                )}
                                <Tooltip
                                    arrow
                                    placement="bottom-start"
                                    title={intl.formatMessage({ id: "dashboard.insights.linkButton.tooltip" })}
                                >
                                    <CircledButton
                                        size={data.isCompleted ? 24 : 28}
                                        color={data.shouldHighlight ? "hovered" : "hovered2"}
                                        component="button"
                                        onClick={() => onClickRelatedButton(data.id, "brief")}
                                    >
                                        <CustomIcon
                                            icon={icons.link}
                                            sx={{
                                                backgroundColor: data.shouldHighlight ? "button.active" : "transparent",
                                                fontSize: 14,
                                                color: "button.active",
                                                "&:hover": {
                                                    backgroundColor: "button.active",
                                                },
                                            }}
                                        />
                                    </CircledButton>
                                </Tooltip>
                            </Box>
                        </Box>
                        {!data.isCompleted && (
                            <Button
                                className={classes.newButton}
                                onClick={
                                    data.isDisabled
                                        ? () => handleGotoBrief(`${urlPaths.Brief}/${companyId}`)
                                        : handleCreateSearchAI
                                }
                            >
                                <CustomIcon icon={data.isDisabled ? icons.pencil : icons.flashLight} />
                                {data.isDisabled ? (
                                    <Typography>
                                        Finish
                                        <br />
                                        Brief
                                    </Typography>
                                ) : (
                                    <Typography>
                                        New
                                        <br />
                                        SearchAI
                                    </Typography>
                                )}
                            </Button>
                        )}
                    </Box>

                    {/* {!data.isCompleted && (
                        <Box>
                            <Typography className={classes.briefSubtitle}>
                                CFO - Sureserve Group PLC (Cap10 Partners)
                            </Typography>
                        </Box>
                    )} */}

                    {/* Options */}
                    {!data.isCompleted && (
                        <Box className={classes.chipsWrapper}>
                            {chips.map((chip) => {
                                const chipType =
                                    SnackBarEntityTypes[
                                        `${chip.label.toLowerCase() as keyof typeof SnackBarEntityTypes}`
                                    ];

                                return (
                                    <Chip
                                        key={chip.id}
                                        className={classes.chip}
                                        variant="filled"
                                        label={chip.label}
                                        size="small"
                                        onClick={(ev) => handleClickCircledCount(ev, chipType, chip)}
                                        onDelete={(ev) => handleClickCircledCount(ev, chipType, chip)}
                                        disabled={!chip.count}
                                        deleteIcon={
                                            <ButtonWithCounter
                                                count={chip.count}
                                                isDisabled={!chip.count}
                                                shouldShowIcon={hoverChipId === chip.id || clickedChipId === chip.id}
                                                size={20}
                                                callback={(ev) => handleClickCircledCount(ev, chipType, chip)}
                                                colors={["hovered", "info"]}
                                            />
                                        }
                                    />
                                );
                            })}
                        </Box>
                    )}
                </>
            </Box>

            <SnackBar
                maxWidth={wrapperRef.current?.getBoundingClientRect().width || 400}
                shouldOpen={shouldOpenSnackBar}
                anchorElement={popOverAnchorEl}
                title={<strong>{capitalize(selectedEntity.type)}</strong>}
                handleInfoClose={(ev) => {
                    handleCloseSnackBar(ev);
                    setSelectedEntity(selectedEntityInitialState);
                    setClickedChipId(null);
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                }}
                {...(wrapperRef.current
                    ? {
                          anchorPosition: {
                              top: wrapperRef.current?.getBoundingClientRect().bottom + 5,
                              left:
                                  wrapperRef.current?.getBoundingClientRect().left +
                                  wrapperRef.current?.getBoundingClientRect().width / 2,
                          },
                      }
                    : {})}
                content={
                    <>
                        <RowHead
                            title={
                                <FormattedMessage
                                    id={`project.dashboard.popup.header.${
                                        selectedEntity.type === SnackBarEntityTypes.company ? "company" : "people"
                                    }.subterm`}
                                />
                            }
                        />
                        <Box sx={{ px: 2, maxHeight: 300, overflowY: "scroll" }}>
                            {selectedEntity.type && !Boolean(selectedEntity.data.length) && <LoadingData />}

                            {selectedEntity.type === SnackBarEntityTypes.company &&
                                (selectedEntity.data as TConvertedCompaniesList[])?.map((c) => (
                                    <Row key={c.id} term={c.name ?? ""} value={c.currentCapitalStructure ?? ""} />
                                ))}

                            {selectedEntity.type === SnackBarEntityTypes.people &&
                                (selectedEntity.data as TConvertedPeopleList[])?.map((p) => (
                                    <Row key={p.id} term={p.name} subTerm={p.jobTitle} value={p.company ?? ""} />
                                ))}

                            {selectedEntity.type === SnackBarEntityTypes.shortlist &&
                                (selectedEntity.data as TConvertedPeopleList[])?.map((p) => (
                                    <Row key={p.id} term={p.name} subTerm={p.jobTitle} value={p.company ?? ""} />
                                ))}
                        </Box>
                    </>
                }
            />
        </>
    );
};

export default BriefCard;
